/**
 * The light lists the pickers read — TRBUD-448.
 *
 * Eden, 25 October 2026: *"Every page is slow to load, the lookup
 * (search-as-you-type) boxes most of all."* The vendor and unit pickers were
 * fed by the list SCREENS' reads: `GET /vendors` sums every vendor's spend this
 * year through the money view, and `GET /assets` builds the whole register row
 * (position, downtime in the last 30 days, spend, inspection due, photos). A
 * picker shows a name and, for a vendor, a status. Measured at production
 * volumes, the vendor list took 90 ms to 600 ms and the unit list 90 ms to
 * 550 ms for a box that cannot be typed into until it answers; these answer in
 * a few milliseconds.
 *
 * The list screens keep their own reads. These two are what a picker needs and
 * nothing else, so there is no money on them and no figure to keep in step
 * with a screen.
 *
 * Browser-safe: types and one pure function.
 */
import { assetClasses, type AssetClassFilter, type VendorDisplayStatus } from "./vocabulary.js";

/**
 * One vendor a picker can offer.
 *
 * The same three fields `VendorTypeahead` has always taken, so a picker can be
 * handed these rows as they are. `displayStatus` is the badge the Vendors
 * screen shows (a one-to-one read of `vendor.status`); the picker uses it to
 * leave out blacklisted and inactive vendors and to mark conditional ones,
 * mirroring the server's rule on new work.
 */
export interface VendorOption {
  vendorId: string;
  name: string;
  displayStatus: VendorDisplayStatus;
}

/** `GET /vendors/options`: every vendor, by name. */
export interface VendorOptionsResponse {
  items: VendorOption[];
}

/**
 * One unit a picker can offer.
 *
 * `unitLabel` is the register's own label (Sylectus code, raw identifier,
 * digits, name), so the picker names a unit the way the Assets screen does.
 * `assetClass` is the stored class, which may be null or a value the contract
 * does not list; the picker groups by `assetClassTab` below, as the register's
 * tabs do.
 */
export interface AssetOption {
  assetId: string;
  unitLabel: string | null;
  assetName: string | null;
  assetClass: string | null;
  status: string;
}

/**
 * `GET /assets/options`: every unit in the register, by unit number, with the
 * count per class tab.
 *
 * Unpaged. The unit picker used to ask for 200 rows of one class at a time, so
 * a class with more than 200 units was cut short with nothing saying so, and
 * every change of class was another request. The whole fleet is a couple of
 * hundred short rows, so the picker holds all of it and switching class is
 * instant.
 */
export interface AssetOptionsResponse {
  items: AssetOption[];
  classCounts: Record<AssetClassFilter, number>;
}

/**
 * The register tab a stored class falls under: the class itself when the
 * contract lists it, otherwise Unclassified. A NULL class and a stored value
 * the contract does not know both land there, so no unit is left out of every
 * tab. The API's register reads the same rule (`tabOfClass` in
 * read/assets.ts), so the picker and the Assets screen cannot disagree about
 * which tab holds a unit.
 */
export function assetClassTab(stored: string | null): AssetClassFilter {
  return stored !== null && (assetClasses as readonly string[]).includes(stored)
    ? (stored as AssetClassFilter)
    : "unclassified";
}
